// src/components/hr/PendingEmployeesModal.tsx
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { X, CheckCircle, UserPlus, AlertCircle, Clock } from 'lucide-react';
import { Employee } from '../../types/firestore';
import { employeesService, PendingEmployee } from '../../services/hrService';
import { auth } from '../../services/firebase';
import LoadingSpinner from '../ui/LoadingSpinner';

interface PendingEmployeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEmployeeActivated: () => void;
  ownerId?: string;
}

const PendingEmployeesModal: React.FC<PendingEmployeesModalProps> = ({
  isOpen,
  onClose,
  onEmployeeActivated,
  ownerId,
}) => {
  const [pendingEmployees, setPendingEmployees] = useState<PendingEmployee[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState<string | null>(null);

  const fetchPendingEmployees = useCallback(async () => {
    if (!ownerId) {
      setPendingEmployees([]);
      return;
    }

    setLoading(true);
    try {
      const employees = await employeesService.getPendingEmployees(ownerId);
      setPendingEmployees(employees as PendingEmployee[]);
    } catch (error) {
      console.error('خطأ في جلب الموظفين المعلقين:', error);
      toast.error('تعذر تحميل الموظفين المعلقين');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    if (isOpen && ownerId) {
      void fetchPendingEmployees();
    }
  }, [fetchPendingEmployees, isOpen, ownerId]);

  const handleActivateEmployee = async (employeeId: string) => {
    if (!auth.currentUser) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }

    setActivating(employeeId);
    try {
      await employeesService.activateEmployee(employeeId, auth.currentUser.uid);

      // تحديث القائمة
      await fetchPendingEmployees();
      onEmployeeActivated();

      toast.success('تم تفعيل حساب الموظف بنجاح');
    } catch (error: any) {
      console.error('خطأ في تفعيل الموظف:', error);
      toast.error(error.message || 'فشل في تفعيل الموظف');
    } finally {
      setActivating(null);
    }
  };

  const getRoleName = (role: Employee['role']) => {
    const roleNames = {
      admin: 'مدير',
      supervisor: 'مشرف',
      assistant: 'مساعد',
      accountant: 'محاسب',
      customer_service: 'خدمة العملاء',
      teacher: 'معلم',
      hr_manager: 'مدير الموارد البشرية',
      employee: 'موظف',
    };
    return roleNames[role] || role;
  };

  const getDepartmentName = (department: Employee['department']) => {
    const departmentNames = {
      management: 'الإدارة',
      teaching: 'التدريس',
      finance: 'المالية',
      hr: 'الموارد البشرية',
      customer_service: 'خدمة العملاء',
      technical: 'التقني',
      administration: 'الإدارة العامة',
    };
    return departmentNames[department] || department;
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b'>
          <div className='flex items-center space-x-3 space-x-reverse'>
            <Clock className='w-6 h-6 text-yellow-600' />
            <h2 className='text-2xl font-bold text-secondary-900'>
              الموظفون المعلقون للتفعيل
            </h2>
            {pendingEmployees.length > 0 && (
              <span className='bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm font-medium'>
                {pendingEmployees.length} معلق
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className='p-2 text-secondary-400 hover:text-secondary-600 rounded-lg hover:bg-secondary-100'
            title='إغلاق'
          >
            <X className='w-6 h-6' />
          </button>
        </div>

        {/* Content */}
        <div className='p-6 max-h-[70vh] overflow-y-auto'>
          {loading ? (
            <div className='flex items-center justify-center py-12'>
              <LoadingSpinner size='large' />
            </div>
          ) : pendingEmployees.length === 0 ? (
            <div className='text-center py-12'>
              <UserPlus className='w-16 h-16 text-secondary-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-secondary-900 mb-2'>
                لا توجد حسابات معلقة
              </h3>
              <p className='text-secondary-600'>
                جميع حسابات الموظفين مفعلة أو لم يتم إنشاء حسابات جديدة
              </p>
            </div>
          ) : (
            <div className='space-y-4'>
              {pendingEmployees.map(employee => (
                <div
                  key={employee.id}
                  className='bg-white border border-secondary-200 rounded-lg p-6 hover:shadow-md transition-shadow'
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <div className='flex items-center space-x-3 space-x-reverse mb-3'>
                        <div className='w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center'>
                          <span className='text-yellow-600 font-semibold text-lg'>
                            {employee.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 className='text-lg font-semibold text-secondary-900'>
                            {employee.name}
                          </h3>
                          <p className='text-sm text-secondary-600'>
                            {employee.employeeId} • {employee.email}
                          </p>
                        </div>
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
                        <div>
                          <span className='text-sm font-medium text-secondary-700'>
                            الدور:
                          </span>
                          <p className='text-sm text-secondary-900'>
                            {getRoleName(employee.role)}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm font-medium text-secondary-700'>
                            القسم:
                          </span>
                          <p className='text-sm text-secondary-900'>
                            {getDepartmentName(employee.department)}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm font-medium text-secondary-700'>
                            المنصب:
                          </span>
                          <p className='text-sm text-secondary-900'>
                            {employee.position}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm font-medium text-secondary-700'>
                            الهاتف:
                          </span>
                          <p className='text-sm text-secondary-900'>
                            {employee.phone}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm font-medium text-secondary-700'>
                            الراتب:
                          </span>
                          <p className='text-sm text-secondary-900'>
                            {employee.salary.base.toLocaleString()}{' '}
                            {employee.salary.currency}
                          </p>
                        </div>
                        <div>
                          <span className='text-sm font-medium text-secondary-700'>
                            تاريخ الإنشاء:
                          </span>
                          <p className='text-sm text-secondary-900'>
                            {(
                              employee.pendingActivation?.createdAt ||
                              employee.createdAt
                            )
                              ?.toDate?.()
                              ?.toLocaleDateString('ar-SA') || 'غير محدد'}
                          </p>
                        </div>
                      </div>

                      <div className='flex items-center space-x-2 space-x-reverse'>
                        <AlertCircle className='w-4 h-4 text-yellow-600' />
                        <span className='text-sm text-yellow-700 bg-yellow-50 px-2 py-1 rounded-full'>
                          في انتظار التفعيل
                        </span>
                      </div>
                    </div>

                    <div className='flex flex-col space-y-2'>
                      <button
                        onClick={() => handleActivateEmployee(employee.id!)}
                        disabled={activating === employee.id}
                        className='btn-success flex items-center space-x-2 space-x-reverse'
                      >
                        {activating === employee.id ? (
                          <>
                            <LoadingSpinner size='small' />
                            <span>جاري التفعيل...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className='w-4 h-4' />
                            <span>تفعيل الحساب</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between p-6 border-t bg-secondary-50'>
          <div className='text-sm text-secondary-600'>
            {pendingEmployees.length > 0 && (
              <>يوجد {pendingEmployees.length} حساب معلق للتفعيل</>
            )}
          </div>
          <button onClick={onClose} className='btn-secondary'>
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingEmployeesModal;
